// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartXYPlaneSettingsAxis.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import Input from 'share/components/controls/Izenda.Input';
import DropDown from 'share/components/controls/Izenda.DropDown';
import NumericInput from 'share/components/controls/Izenda.NumericInput';
import Font from 'share/components/controls/Izenda.Font';
import Button from 'share/components/controls/Izenda.Button';
import ReportPartChartConstants from 'logic/data/Izenda.ReportPartChartConstants';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import { DEFAULT_DEBOUNCE_TIME } from 'logic/data/Izenda.UIConstants';
import localizer from 'share/localization/Izenda.Localizer';
import DateTimePickerByDateElement from 'share/components/controls/Izenda.DateTimePickerByDateElement';
import { get, debounce, invoke, isNaN } from 'lodash/fp';
const CONSTANTS = ReportPartChartConstants.CHART_SETTINGS.XY_PLANE;
const MAX_SIGN_INT = Math.pow(2, 32);

const NumericInputWithTooltip = (props: any) => (
  <div className='row input-with-info'>
    <span className='col-md-4'>{props.label}</span>
    <div className='col-md-8'>
      <NumericInput
        value={props.value}
        isDecimal={props.dataType !== DATA_TYPES.DATE_TIME}
        min={0.0001}
        max={MAX_SIGN_INT}
        showInfoIcon={true}
        hint={props.hint}
        onChange={props.onChange}
      />
    </div>
  </div>
);

const StartingPoint = (props: any) => {
  if (props.isCategory) {
    return null;
  }

  const renderControl = () => {
    const dataType = props.dataType;
    const isDisabled = dataType === DATA_TYPES.TEXT || dataType === DATA_TYPES.IMAGE;

    return dataType === DATA_TYPES.DATE_TIME && !isDisabled ? (
      <div className='numeric-input show-icon'>
        <DateTimePickerByDateElement
          className='u_floatLeft'
          isDecimal={false}
          value={props.value}
          onChange={props.onChange}
          groupByDateTime={get('dateTimeFormatData.groupBy', props.selectedAxis)}
        />
        <a>
          <span className='icon-info-circled' title={props.hint} />
        </a>
      </div>
    ) : (
      <NumericInput
        value={props.value}
        max={MAX_SIGN_INT}
        showInfoIcon
        hint={props.hint}
        disabled={isDisabled}
        onChange={props.onChange}
      />
    );
  };

  return (
    <div className='row input-with-info'>
      <span className='col-md-4'>{props.label}</span>
      <div className='col-md-8'>{renderControl()}</div>
    </div>
  );
};

class Axis extends Component {
  constructor(props: any) {
    super(props);
    this.onChangeWithDebounce = debounce(DEFAULT_DEBOUNCE_TIME, props.onChange);

    const { text, enabled, font, alignment, intervals, categoryIntervals, startingPoint, isCategory } = props.value;

    // due to dynamic structure of state, we cannot use mobx here
    this.state = {
      text,
      enabled,
      font,
      alignment,
      intervals,
      categoryIntervals,
      startingPoint,
      isCategory,
      disabledSyncButton: true
    };
  }

  componentWillReceiveProps(nextProps: any) {
    const { text, enabled, font, alignment, intervals, categoryIntervals, startingPoint, isCategory } =
      nextProps.value || {};
    this.setState({
      text,
      enabled,
      font,
      alignment,
      intervals,
      categoryIntervals,
      startingPoint,
      isCategory,
      disabledSyncButton: invoke('disabledSyncButton', nextProps)
    });
  }

  /**
   * Handle change of state
   * @param {string} key
   * @param {Object} value
   * @param {boolean} useDebounce
   */
  changeState = (key: string, value: any, useDebounce: boolean) => {
    const newState = { [key]: value };

    this.setState(newState, () => {
      useDebounce ? this.onChangeWithDebounce({ value: this.state }) : this.props.onChange({ value: this.state });
    });
  };

  /**
   * Handle on change of Enabled
   * @param {Object} e
   */
  handleEnabledChange = (e: any) => {
    this.changeState('enabled', e.target.checked);
  };

  /**
   * Handle on change of Title
   * @param {Object} e
   */
  handleTitleChange = (e: any) => {
    this.changeState('text', e.target.value, true);
  };

  /**
   * Handle on change of Font
   * @param {Object} font
   */
  handleFontChange = (font: any) => {
    this.changeState('font', font.value);
  };

  /**
   * Handle on change of Alignment
   * @param {Object} newOption
   */
  handleAlignmentChanged = (newOption: any) => {
    this.changeState('alignment', newOption.value);
  };

  /**
   * Handle on change of Tick Interval
   * @param {Object} value
   */
  handleTickIntervalChanged = (value: any) => {
    if (isNaN(value)) {
      return;
    }
    const intervalKey = this.state.isCategory ? 'categoryIntervals' : 'intervals';
    this.changeState(intervalKey, value, true);
  };

  /**
   * Handle on change of Starting Point
   * @param {Object} value
   */
  handleStartingPointChanged = (value: any) => {
    if (isNaN(value)) {
      return;
    }
    value === 'Invalid date' && (value = null);
    this.changeState('startingPoint', value, true);
  };

  /**
   * Update XAxis Type
   * @param {Object} value
   */
  setXAxisType = (value: any) => {
    this.props.setXAxisType(value);
    this.setState({
      isCategory: value,
      disabledSyncButton: invoke('disabledSyncButton', this.props)
    });
  };

  /**
   * Switch to Linear
   */
  switchToLinear = () => {
    this.state.isCategory && this.setXAxisType(false);
  };

  /**
   * Switch to Category
   */
  switchToCategory = () => {
    !this.state.isCategory && this.setXAxisType(true);
  };

  /**
   * Sync xAxis Type
   */
  syncXAxisType = () => {
    this.props.syncXAxisType(this.state.isCategory);
    this.setState({ disabledSyncButton: true });
  };

  /**
   * Resolve label field
   */
  getLabelField = (option: any) => {
    return localizer.getText(['XY_PLANE_TITLE_OPTION', this.props.type, option.value].join('_'));
  };

  /**
   * Render xAxis type
   */
  renderxAxisType = () => {
    const state = this.state;
    const value = this.props.value;
    return (
      value.dataType !== DATA_TYPES.TEXT && (
        <div className='row input-with-info'>
          <span className='col-md-4'>{localizer.getText('XY_PLANE_LABEL_XAXIS_TYPE')}</span>
          <div className='col-md-8'>
            <div className='col-md-6 u_noPaddingLeft'>
              <Input type='radio' checked={!state.isCategory} onChange={this.switchToLinear} />
              &nbsp;{localizer.getText('XY_PLANE_LABEL_LINEAR')}
              <a title={localizer.getText('XY_PLANE_LABEL_LINEAR_TOOLTIP')} className='icon-help-circled' />
            </div>
            <div className='col-md-6'>
              <Input type='radio' checked={state.isCategory} onChange={this.switchToCategory} />
              &nbsp;{localizer.getText('XY_PLANE_LABEL_VALUE')}
              <a title={localizer.getText('XY_PLANE_LABEL_VALUE_TOOLTIP')} className='icon-help-circled' />
            </div>
            <Button
              label={localizer.getText('XY_PLANE_LABEL_APPLY_ALL')}
              className='u_marginTop'
              onClick={this.syncXAxisType}
              disabled={state.disabledSyncButton}
            />
          </div>
        </div>
      )
    );
  };

  render() {
    const {
      font,
      isCategory,
      categoryIntervals,
      intervals: _intervals,
      enabled,
      text,
      alignment,
      startingPoint
    } = this.state;
    const { value, type, selectedAxis } = this.props;
    const HINT = CONSTANTS[type].TOOLTIP || {};
    const ALIGN_DROPDOWN_OPTION = CONSTANTS.XY_PLANE_TITLE_OPTION;
    const intervals = isCategory ? categoryIntervals : _intervals;

    return (
      <div className='axis u_fullWidth'>
        <div className='row title'>
          <span className='col-md-4'>{localizer.getText('COMMON_LABEL_TITLE')}</span>
          <div className='col-md-8'>
            <Input type='checkbox' checked={enabled} onChange={this.handleEnabledChange} />
            <Input disabled={!enabled} value={text} onChange={this.handleTitleChange} />
          </div>
        </div>
        <div className='row xyplane-font-setting'>
          <span className='col-md-4'>{localizer.getText('FONT_SETTINGS_LABEL_FONT')}</span>
          <div className='col-md-8'>
            <Font value={font} disabled={!enabled} onChange={this.handleFontChange} />
          </div>
        </div>
        <div className='row alignment'>
          <span className='col-md-4'>{localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT')}</span>
          <div className='col-md-8'>
            <DropDown
              disabled={!enabled}
              options={ALIGN_DROPDOWN_OPTION}
              value={alignment}
              labelField={this.getLabelField}
              onChange={this.handleAlignmentChanged}
            />
          </div>
        </div>
        {type === 'xAxis' && this.renderxAxisType()}
        <NumericInputWithTooltip
          value={intervals}
          dataType={value.dataType}
          label={localizer.getText('REPORTDESIGNER_LABEL_INTERVALS')}
          onChange={this.handleTickIntervalChanged}
          hint={HINT.INTERVALS}
        />
        <StartingPoint
          value={startingPoint}
          dataType={value.dataType}
          label={localizer.getText('REPORTDESIGNER_LABEL_STARTING_POINT')}
          onChange={this.handleStartingPointChanged}
          selectedAxis={selectedAxis}
          hint={HINT.STARTING_POINT}
          isCategory={isCategory}
        />
      </div>
    );
  }
}

export default Axis;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartXYPlaneSettingsAxis.js